<script setup>
// Config do nó Condição — modos Simples/Avançado, campo/operador/valor (com label especial).
// Espelha ConditionConfig do original. Saídas: Sim (true) / Não (false).
import { computed, inject, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import FlowField from './FlowField.vue';

const props = defineProps({ node: { type: Object, required: true } });

const { t } = useI18n();

const aux = inject('flowAux', {});
const d = computed(() => props.node.data);

const FIELDS = computed(() => [
  { value: 'message', label: t('CHATBOT.nodeConfig.condition.field_message') },
  {
    value: 'senderName',
    label: t('CHATBOT.nodeConfig.condition.field_senderName'),
  },
  {
    value: 'contactEmail',
    label: t('CHATBOT.nodeConfig.condition.field_contactEmail'),
  },
  { value: 'inboxId', label: t('CHATBOT.nodeConfig.condition.field_inboxId') },
  {
    value: 'response',
    label: t('CHATBOT.nodeConfig.condition.field_response'),
  },
  { value: '__label', label: t('CHATBOT.nodeConfig.condition.field_label') },
]);
const OPERATORS = computed(() => [
  { value: 'contains', label: t('CHATBOT.nodeConfig.condition.op_contains') },
  { value: 'equals', label: t('CHATBOT.nodeConfig.condition.op_equals') },
  { value: 'notEquals', label: t('CHATBOT.nodeConfig.condition.op_notEquals') },
  {
    value: 'startsWith',
    label: t('CHATBOT.nodeConfig.condition.op_startsWith'),
  },
  { value: 'endsWith', label: t('CHATBOT.nodeConfig.condition.op_endsWith') },
  {
    value: 'notContains',
    label: t('CHATBOT.nodeConfig.condition.op_notContains'),
  },
  {
    value: 'greaterThan',
    label: t('CHATBOT.nodeConfig.condition.op_greaterThan'),
  },
  { value: 'lessThan', label: t('CHATBOT.nodeConfig.condition.op_lessThan') },
]);
const LABEL_OPERATORS = computed(() => [
  { value: 'hasLabel', label: t('CHATBOT.nodeConfig.condition.op_hasLabel') },
  {
    value: 'notHasLabel',
    label: t('CHATBOT.nodeConfig.condition.op_notHasLabel'),
  },
]);

const isLabel = computed(() => d.value.field === '__label');
const mode = computed({
  get: () => d.value.conditionType || 'simple',
  set: v => {
    d.value.conditionType = v;
  },
});
const labelOptions = computed(() => [
  { value: '', label: t('CHATBOT.nodeConfig.condition.selectLabel') },
  ...(aux.labels || []).map(l => ({ value: l.title, label: l.title })),
]);

const onFieldChange = v => {
  d.value.field = v;
  d.value.operator = v === '__label' ? 'hasLabel' : 'contains';
  d.value.value = '';
};

onMounted(() => {
  if (!d.value.field) d.value.field = 'message';
  if (!d.value.operator) d.value.operator = 'contains';
  if (!d.value.conditionType) d.value.conditionType = 'simple';
});
</script>

<template>
  <div>
    <div class="flex gap-1 mb-3">
      <button
        class="flex-1 px-2 py-1.5 text-xs rounded-md border border-n-weak text-n-slate-11 bg-n-background"
        :class="{ 'bg-n-brand text-white border-n-brand': mode === 'simple' }"
        @click="mode = 'simple'"
      >
        {{ t('CHATBOT.nodeConfig.condition.modeSimple') }}
      </button>
      <button
        class="flex-1 px-2 py-1.5 text-xs rounded-md border border-n-weak text-n-slate-11 bg-n-background"
        :class="{ 'bg-n-brand text-white border-n-brand': mode === 'advanced' }"
        @click="mode = 'advanced'"
      >
        {{ t('CHATBOT.nodeConfig.condition.modeAdvanced') }}
      </button>
    </div>

    <template v-if="mode === 'simple'">
      <FlowField
        :model-value="d.field"
        :label="t('CHATBOT.nodeConfig.condition.fieldLabel')"
        type="select"
        :options="FIELDS"
        @update:model-value="onFieldChange"
      />
      <FlowField
        :model-value="d.operator"
        :label="t('CHATBOT.nodeConfig.condition.operatorLabel')"
        type="select"
        :options="isLabel ? LABEL_OPERATORS : OPERATORS"
        @update:model-value="d.operator = $event"
      />
      <FlowField
        v-if="isLabel"
        :model-value="d.value"
        :label="t('CHATBOT.nodeConfig.condition.valueLabel_label')"
        type="select"
        :options="labelOptions"
        @update:model-value="d.value = $event"
      />
      <FlowField
        v-else
        :model-value="d.value"
        :label="t('CHATBOT.nodeConfig.condition.valueLabel')"
        type="text"
        placeholder="Ex: urgente"
        @update:model-value="d.value = $event"
      />
    </template>

    <template v-else>
      <FlowField
        :model-value="d.condition"
        :label="t('CHATBOT.nodeConfig.condition.customConditionLabel')"
        type="textarea"
        :rows="4"
        placeholder="message.includes('urgente') && inboxId == 1"
        :hint="t('CHATBOT.nodeConfig.condition.jsExpressionHint')"
        @update:model-value="d.condition = $event"
      />
      <div
        class="p-2 text-[0.6875rem] rounded bg-n-alpha-1 border border-n-weak text-n-slate-11"
      >
        {{ t('CHATBOT.nodeConfig.condition.availableVarsTitle') }}
        <code>{{ 'message' }}</code
        >, <code>{{ 'senderName' }}</code
        >, <code>{{ 'contactEmail' }}</code
        >, <code>{{ 'inboxId' }}</code
        >, <code>{{ 'response' }}</code>
      </div>
    </template>

    <p class="mt-3 text-xs text-n-slate-10">
      <strong class="text-n-teal-11">{{
        t('CHATBOT.nodes.condition.yes')
      }}</strong>
      ·
      <strong class="text-n-ruby-11">{{
        t('CHATBOT.nodes.condition.no')
      }}</strong>
    </p>
  </div>
</template>
